/**
 * AddBusinessDialog — modal for creating a new business inside the tenant.
 *
 * Form fields:
 *   • Name (required)
 *   • Slug (auto-derived from name; editable)
 *   • Currency (defaults to GHS)
 *   • Industry (defaults to retail)
 *   • Description (optional)
 *
 * Plan-tier limit is enforced server-side. If the tenant is on a
 * non-Enterprise plan, the API returns 402 with a helpful message
 * which we surface as a toast.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <Button onClick={() => setOpen(true)}>Add business</Button>
 *   <AddBusinessDialog open={open} onOpenChange={setOpen} />
 *
 * Authority: tenant_owner only. Hide the trigger button for other roles.
 */
import { useEffect, useMemo, useState } from "react";
import { Building2, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useCreateBusiness } from "@/hooks/useCreateBusiness";
import useBusinessStore from "@/stores/businessStore";
import { COUNTRIES, currencyInfo, getCurrencyCode } from "@/lib/currency";

// Every currency of the countries we sell in, plus USD/EUR/GBP.
const CURRENCIES = [...new Set([...COUNTRIES.map((c) => c.currency), "USD", "EUR", "GBP"])].map((code) => ({
  code,
  label: `${currencyInfo(code).name} (${code})`,
}));

const INDUSTRIES = [
  { value: "retail", label: "Retail" },
  { value: "restaurant", label: "Restaurant" },
  { value: "salon", label: "Salon / Spa" },
  { value: "hardware", label: "Hardware" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "wholesale", label: "Wholesale / Distribution" },
  { value: "services", label: "Professional services" },
  { value: "construction", label: "Construction" },
  { value: "farm", label: "Farm / Agriculture" },
  { value: "other", label: "Other" },
];

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

interface AddBusinessDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Optional: after create, switch to the new business automatically */
  switchOnCreate?: boolean;
}

export default function AddBusinessDialog({
  open,
  onOpenChange,
  switchOnCreate = true,
}: AddBusinessDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  // A new business starts in the currency of the one open now.
  const [currency, setCurrency] = useState(() => getCurrencyCode());
  const [industry, setIndustry] = useState("retail");
  const [description, setDescription] = useState("");

  const setActiveBusinessSlug = useBusinessStore(
    (s) => s.setActiveBusinessSlug,
  );

  const createMutation = useCreateBusiness();

  // Auto-derive slug from name unless user has edited it manually
  const autoSlug = useMemo(() => slugify(name), [name]);
  useEffect(() => {
    if (!slugTouched) setSlug(autoSlug);
  }, [autoSlug, slugTouched]);

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setCurrency(getCurrencyCode());
    setIndustry("retail");
    setDescription("");
  }
  function close(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const canSubmit = name.trim().length > 0 && !createMutation.isPending;

  async function onSubmit() {
    if (!canSubmit) return;
    try {
      const created = await createMutation.mutateAsync({
        name: name.trim(),
        slug: slug.trim() || undefined,
        currency,
        industry,
        description: description.trim() || undefined,
      });
      toast.success(`${created.name} created`);

      if (switchOnCreate) {
        setActiveBusinessSlug(created.slug);
      }
      close(false);
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status?: number;
          data?: { message?: string; errors?: Array<{ msg?: string }> };
        };
      };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.msg ||
        "Could not create business";
      if (err?.response?.status === 402) {
        // Plan-tier limit hit
        toast.error(msg, {
          description: "Add an extra business in Settings → Subscription (Growth and Business plans), or talk to us about Enterprise.",
          duration: 8000,
        });
      } else {
        toast.error(msg);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#4CAF50]" />
            Add a business
          </DialogTitle>
          <DialogDescription>
            Set up another business inside your tenant. Each business gets its
            own products, cashiers, accounting books, and reports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="biz-name">
              Business name <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="biz-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Ventures"
              autoFocus
              maxLength={100}
            />
          </div>

          {/* Slug */}
          <div className="space-y-1.5">
            <Label htmlFor="biz-slug" className="flex items-center gap-1.5">
              URL slug
              <Sparkles className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-normal text-muted-foreground">
                auto-generated
              </span>
            </Label>
            <Input
              id="biz-slug"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugTouched(true);
              }}
              placeholder="acme-ventures"
              pattern="[a-z0-9-]+"
            />
            <p className="text-[11px] text-muted-foreground">
              Used in URLs and reports. Lowercase letters, digits, and hyphens
              only.
            </p>
          </div>

          {/* Currency + Industry */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="biz-currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="biz-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger id="biz-industry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="biz-desc">Description (optional)</Label>
            <Textarea
              id="biz-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What does this business do?"
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row">
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Creating…
              </>
            ) : (
              "Create business"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
